import React from "react";
import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Download, Package, MessageCircle } from "lucide-react";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { api, formatINR } from "@/lib/api";
import { WHATSAPP_NUMBER } from "@/components/WhatsAppFab";

export default function OrderSuccess() {
  const { id } = useParams();
  const { data: order, isLoading } = useQuery({ queryKey: ["order", id], queryFn: () => api.getOrder(id) });

  const waMsg = order
    ? `Hi Altos World! I've placed order ${order.id.slice(0, 8).toUpperCase()} for ${formatINR(order.total_billing || order.amount)}.`
    : "";

  return (
    <div className="min-h-screen bg-paper">
      <Header />
      <main className="mx-auto max-w-2xl px-4 sm:px-6 py-12">
        <div className="text-center fade-up">
          <div className="w-20 h-20 rounded-full bg-brandter flex items-center justify-center mx-auto">
            <CheckCircle2 className="text-ok" size={44} />
          </div>
          <h1 className="font-display text-4xl sm:text-5xl text-ink mt-5">Order Confirmed!</h1>
          <p className="text-muted2 mt-2">Thank you for shopping with Altos World. We'll update you on WhatsApp.</p>
        </div>

        {isLoading ? (
          <p className="text-center text-muted2 mt-8">Loading order…</p>
        ) : order ? (
          <div className="mt-8 rounded-2xl border border-line p-6" data-testid="order-summary">
            <div className="flex items-center justify-between border-b border-line pb-3 mb-3">
              <span className="text-sm text-muted2">Order ID</span>
              <span className="font-mono font-semibold text-ink text-sm">{order.id.slice(0, 8).toUpperCase()}</span>
            </div>
            <div className="space-y-3">
              {order.items.map((it, i) => (
                <div key={`${it.name}-${i}`} className="flex items-center justify-between text-sm">
                  <span className="text-ink">{it.name} × {it.quantity}</span>
                  <span className="font-medium text-ink">{formatINR(it.line_total)}</span>
                </div>
              ))}
            </div>
            <div className="border-t border-line mt-3 pt-3 space-y-1.5 text-sm">
              <div className="flex justify-between"><span className="text-muted2">Subtotal</span><span>{formatINR(order.subtotal)}</span></div>
              {order.shipping_charge > 0 && <div className="flex justify-between"><span className="text-muted2">Shipping</span><span>{formatINR(order.shipping_charge)}</span></div>}
              {order.discount > 0 && <div className="flex justify-between text-ok"><span>Discount</span><span>- {formatINR(order.discount)}</span></div>}
              <div className="flex justify-between font-semibold text-ink text-base"><span>Total</span><span>{formatINR(order.total_billing || order.amount)}</span></div>
              {order.payment_mode === "partial_cod" && (
                <>
                  <div className="flex justify-between text-muted2"><span>Paid now</span><span>{formatINR(order.advance_amount)}</span></div>
                  <div className="flex justify-between text-warn font-semibold"><span>Balance on delivery</span><span>{formatINR(order.cod_due)}</span></div>
                </>
              )}
            </div>

            <div className="grid sm:grid-cols-2 gap-3 mt-6">
              <a href={api.invoiceUrl(order.id)} target="_blank" rel="noopener noreferrer" className="h-11 rounded-full border border-line flex items-center justify-center gap-2 text-sm font-semibold text-ink" data-testid="download-invoice">
                <Download size={16} /> Download invoice
              </a>
              <a href={`https://wa.me/${WHATSAPP_NUMBER}?text=${encodeURIComponent(waMsg)}`} target="_blank" rel="noopener noreferrer" className="h-11 rounded-full bg-[#25D366] text-white flex items-center justify-center gap-2 text-sm font-semibold" data-testid="whatsapp-confirm">
                <MessageCircle size={16} /> Confirm on WhatsApp
              </a>
            </div>
          </div>
        ) : (
          <p className="text-center text-err mt-8">Order not found.</p>
        )}

        <div className="flex items-center justify-center gap-4 mt-8">
          <Link to="/orders" className="text-sm font-semibold text-brand flex items-center gap-1"><Package size={16} /> Track my orders</Link>
          <span className="text-line">·</span>
          <Link to="/" className="text-sm font-semibold text-ink">Continue shopping</Link>
        </div>
      </main>
      <Footer />
    </div>
  );
}
